import { c as _c } from "react/compiler-runtime";
import { Box, Stack } from "@mui-wrapper";
import { DemoTag } from "@src/components/common/demo-tag";
import { EmptyPage } from "@src/components/common/empty-page/empty-page";
import { GroupsDuoIcon, Text } from "@flat-ui";
import { PageContainer } from "@components/common/page-container";
import { ThumbnailImage } from "@components/common/thumbnail-image";
import { ExternalLinks } from "@components/external-links";
import { CreateGroup } from "@components/groups/create-group";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { generateGroupDetailsRoute } from "@router/router-helper";
import { userMenuLink, withEllipsisTwoLines } from "@styles/common-styles";
import { UserMenuEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import defaultGroupThumbnail from "@assets/thumbnails/groups-thumbs/group-thumbnail_default.svg";
/** Show this specific empty page when workspace has only the demo group */
export function GroupsDemoEmptyPage(t0) {
  const $ = _c(42);
  const {
    companyId,
    demoGroup
  } = t0;
  const {
    navigateToGroupDetails
  } = useAppNavigation();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    canCreateGroups
  } = useHasUserValidRoleCompanyLevel();
  let t1;
  if ($[0] !== companyId || $[1] !== demoGroup.id) {
    t1 = generateGroupDetailsRoute(companyId, demoGroup.id);
    $[0] = companyId;
    $[1] = demoGroup.id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      ...userMenuLink,
      color: "primary.main",
      textDecoration: "underline",
      "&:hover": {
        textDecoration: "underline !important"
      }
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== demoGroup.id || $[5] !== trackEvent) {
    t3 = () => {
      trackEvent({
        name: UserMenuEvents.openGroupDetails,
        props: {
          origin: "Empty groups page",
          type: "demo",
          groupId: demoGroup.id
        }
      });
    };
    $[4] = demoGroup.id;
    $[5] = trackEvent;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = <Box component={Link} to={t1} sx={t2} onClick={t3}>demo</Box>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const demoGroupLink = t4;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      color: "text.secondary"
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Text variant="bodyS" sx={t5}>Create and manage groups in your workspace. Groups need to have at least one group manager. <ExternalLinks link="managingGroupsHelp" /></Text>;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      width: "100%",
      height: "70px",
      mt: "15px",
      py: "10px",
      px: "20px",
      backgroundColor: "surfaces.secondary",
      "&:hover": {
        backgroundColor: "surfaces.tertiary",
        "& .actionButtons": {
          visibility: "visible"
        }
      },
      cursor: "pointer",
      justifyContent: "space-between",
      alignItems: "center"
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== companyId || $[14] !== demoGroup.id || $[15] !== navigateToGroupDetails) {
    t8 = () => navigateToGroupDetails({
      companyId,
      groupId: demoGroup.id
    });
    $[13] = companyId;
    $[14] = demoGroup.id;
    $[15] = navigateToGroupDetails;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      maxHeight: "50px",
      maxWidth: "70px",
      objectFit: "contain",
      mx: "20px"
    };
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== demoGroup.thumbnailUrl) {
    t10 = <ThumbnailImage src={demoGroup.thumbnailUrl} fallbackSrc={defaultGroupThumbnail} alt="Group thumbnail" sx={t9} />;
    $[18] = demoGroup.thumbnailUrl;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      paddingRight: "5px"
    };
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Text variant="heading12" component="div" sx={{
      color: "text.primary",
      mb: "7px"
    }}>Demo</Text>;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Box component="div" sx={t11}>{t12}<Text variant="bodyS" sx={{
        color: "text.secondary",
        ...withEllipsisTwoLines
      }}>Customise and explore our demo group</Text></Box>;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t10) {
    t14 = <Stack direction="row">{t10}{t13}</Stack>;
    $[23] = t10;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      alignItems: "center"
    };
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Stack direction="row" sx={t15}><DemoTag sx={{
        mr: "20px"
      }} /></Stack>;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== t14 || $[28] !== t8) {
    t17 = <Stack sx={t7} direction="row" onClick={t8}>{t14}{t16}</Stack>;
    $[27] = t14;
    $[28] = t8;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== canCreateGroups || $[31] !== demoGroupLink) {
    t18 = canCreateGroups ? <Box component="p">Explore the {demoGroupLink} to discover the Sphere XG features. Feel free to add your own group.</Box> : <>You currently don't have any groups. To get started, explore and try out our {demoGroupLink}.</>;
    $[30] = canCreateGroups;
    $[31] = demoGroupLink;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== canCreateGroups || $[34] !== demoGroup) {
    t19 = canCreateGroups && <CreateGroup groups={[demoGroup]} />;
    $[33] = canCreateGroups;
    $[34] = demoGroup;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t18 || $[37] !== t19) {
    t20 = <EmptyPage title="You don't have a group yet" subtitle={t18} icon={GroupsDuoIcon} button={t19} />;
    $[36] = t18;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t17 || $[40] !== t20) {
    t21 = <PageContainer title="Groups Management" description={t6}>{t17}{t20}</PageContainer>;
    $[39] = t17;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  return t21;
}